/**
 * OaSIS Explorer Frontend - Main App Component
 * Task 3.1: Frontend Foundation & Global Features
 * Task 30.2: Added auth state cache invalidation
 * Task 32.6: Added version-prefixed routing
 * 
 * Root application component with routing and global providers
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth } from './config/firebase';
import { trpc, trpcClient } from './api';
import { useDefaultVersion } from './hooks';
import { LanguageProvider, ThemeProvider, AuthProvider } from './contexts';
import { Layout, ErrorBoundary, ProtectedRoute } from './components';

const HomePage = lazy(() => import('./pages/HomePage').then((module) => ({ default: module.HomePage })));
const AdvancedSearchPage = lazy(() =>
  import('./pages/AdvancedSearchPage').then((module) => ({ default: module.AdvancedSearchPage }))
);
const ClassificationPage = lazy(() =>
  import('./pages/ClassificationPage').then((module) => ({ default: module.ClassificationPage }))
);
const SCTPage = lazy(() => import('./pages/SCTPage').then((module) => ({ default: module.SCTPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then((module) => ({ default: module.AboutPage })));
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((module) => ({ default: module.NotFoundPage }))
);
const OccupationDetailsPage = lazy(() =>
  import('./pages/OccupationDetailsPage').then((module) => ({ default: module.OccupationDetailsPage }))
);
const UserPage = lazy(() => import('./pages/UserPage').then((module) => ({ default: module.UserPage })));
const ResultsPage = lazy(() => import('./pages/ResultsPage').then((module) => ({ default: module.ResultsPage })));
const SignInPage = lazy(() => import('./pages/SignInPage').then((module) => ({ default: module.SignInPage })));
const SignUpPage = lazy(() => import('./pages/SignUpPage').then((module) => ({ default: module.SignUpPage })));
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then((module) => ({ default: module.ForgotPasswordPage }))
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((module) => ({ default: module.ProfilePage }))
);
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((module) => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then((module) => ({ default: module.TermsPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then((module) => ({ default: module.ContactPage })));

/**
 * ScrollToTop component - scrolls to top on route change
 */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

/**
 * AuthCacheInvalidator - Invalidates query cache when auth state changes
 * This ensures cached user data (subscription, profile) is refreshed on login/logout
 */
const AuthCacheInvalidator: React.FC = () => {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    let previousUserId: string | null = null;
    
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      const currentUserId = user?.uid || null;
      
      // Only invalidate if user actually changed (not just token refresh)
      if (currentUserId === previousUserId) {
        return;
      }
      
      previousUserId = currentUserId;
      
      // Wait a moment for the auth token to be ready in the tRPC client
      // This ensures subsequent queries will have the correct Authorization header
      if (user) {
        await user.getIdToken(); // Force token to be ready
      }
      
      // Clear all cached queries - this forces fresh fetches with the new auth token
      queryClient.clear();
    });
    
    return () => unsubscribe();
  }, [queryClient]);
  
  return null;
};

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

/**
 * Helper component for route redirects (backward compatibility)
 */
const VersionedRedirect: React.FC<{ target: 'search' | 'results' | 'classification' | 'sct' }> = ({ target }) => {
  const { defaultVersion, isLoading } = useDefaultVersion();
  const location = useLocation();
  if (isLoading || !defaultVersion) return null;
  return <Navigate to={`/${defaultVersion}/${target}${location.search}`} replace />;
};

const OccupationRedirect: React.FC = () => {
  const { defaultVersion, isLoading } = useDefaultVersion();
  const { code } = useParams();
  const location = useLocation();
  if (isLoading || !defaultVersion) return null;
  return <Navigate to={`/${defaultVersion}/occupation/${code}${location.search}`} replace />;
};

const RouteLoadingFallback: React.FC = () => (
  <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
);

const renderLazyPage = (PageComponent: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<RouteLoadingFallback />}>
    <PageComponent />
  </Suspense>
);

/**
 * Main App component with all providers and routing
 * Task 32.6: Added version-prefixed routes with backward compatibility redirects
 */
export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <LanguageProvider>
              <AuthProvider>
              <AuthCacheInvalidator />
              <BrowserRouter>
                <ScrollToTop />
                <Routes>
                  <Route path="/" element={<Layout />}>
                    {/* Version-agnostic routes */}
                    <Route index element={renderLazyPage(HomePage)} />
                    <Route path="about" element={renderLazyPage(AboutPage)} />
                    <Route path="privacy" element={renderLazyPage(PrivacyPage)} />
                    <Route path="terms" element={renderLazyPage(TermsPage)} />
                    <Route path="contact" element={renderLazyPage(ContactPage)} />
                    <Route path="user" element={<ProtectedRoute>{renderLazyPage(UserPage)}</ProtectedRoute>} />
                    <Route path="profile" element={<ProtectedRoute>{renderLazyPage(ProfilePage)}</ProtectedRoute>} />
                    <Route path="signin" element={renderLazyPage(SignInPage)} />
                    <Route path="signup" element={renderLazyPage(SignUpPage)} />
                    <Route path="forgot-password" element={renderLazyPage(ForgotPasswordPage)} />
                    
                    {/* Version-prefixed routes (Task 32.6) */}
                    <Route path=":version/search" element={renderLazyPage(AdvancedSearchPage)} />
                    <Route path=":version/results" element={renderLazyPage(ResultsPage)} />
                    <Route path=":version/classification" element={renderLazyPage(ClassificationPage)} />
                    <Route path=":version/sct" element={renderLazyPage(SCTPage)} />
                    <Route path=":version/occupation/:code" element={renderLazyPage(OccupationDetailsPage)} />
                    
                    {/* Backward compatibility redirects (Task 32.6) */}
                    <Route path="search" element={<VersionedRedirect target="search" />} />
                    <Route path="results" element={<VersionedRedirect target="results" />} />
                    <Route path="classification" element={<VersionedRedirect target="classification" />} />
                    <Route path="sct" element={<VersionedRedirect target="sct" />} />
                    <Route path="occupation/:code" element={<OccupationRedirect />} />
                    
                    {/* 404 */}
                    <Route path="*" element={renderLazyPage(NotFoundPage)} />
                  </Route>
                </Routes>
              </BrowserRouter>
              </AuthProvider>
            </LanguageProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  );
};